import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import enTranslations from '@/locales/en.json';
import viTranslations from '@/locales/vi.json';
import { TranslationKeys } from '@/types/i18n';

export type Language = 'vi' | 'en';

interface I18nStore {
  language: Language;
  setLanguage: (lang: Language) => void;
}

// Store cho ngôn ngữ
const useI18nStore = create<I18nStore>()(
  persist(
    (set) => ({
      language: 'vi', // Ngôn ngữ mặc định
      setLanguage: (lang) => set({ language: lang }),
    }),
    {
      name: 'language-storage', // tên của key trong localStorage
    }
  )
);

// Lấy ngôn ngữ hiện tại
const getCurrentLanguage = (): Language => {
  return useI18nStore.getState().language;
};

// Hook sử dụng để dịch
export const useTranslation = () => {
  const { language, setLanguage } = useI18nStore();
  
  const t = (key: string, params?: Record<string, string | number>): string => {
    let translation = getTranslation(key, language);
    
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(`{{${paramKey}}}`, String(paramValue));
      });
    }
    
    return translation;
  };
  
  return { t, language, setLanguage };
};

// Hàm dịch không sử dụng hook, cho các trường hợp đặc biệt
export const t = (key: string, params?: Record<string, string | number>): string => {
  const language = getCurrentLanguage();
  let translation = getTranslation(key, language);
  
  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translation = translation.replace(`{{${paramKey}}}`, String(paramValue));
    });
  }
  
  return translation;
};

// Lấy bản dịch từ file json
const getTranslation = (key: string, lang: Language): string => {
  const keys = key.split('.');
  
  // Chọn file ngôn ngữ
  const translations = lang === 'en' ? enTranslations : viTranslations;
  
  // Đi sâu vào object để tìm bản dịch
  let result: any = translations;
  for (const k of keys) {
    if (!result[k]) {
      return key; // Trả về key nếu không tìm thấy bản dịch
    }
    result = result[k];
  }
  
  return result;
};

export default useI18nStore; 